import { useEffect, useRef, useState } from 'react';
import { Building2, FolderOpen, Loader2, Search, UserRound, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  ensureContactDocumentSpace, listDocumentContactOptions,
  type DocumentContactOptionsPage,
} from '../../services/documents';
import type { DocumentSpace } from '../../types/documents';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (space: DocumentSpace) => void;
}

export function OpenClientDossierDialog({ open, onOpenChange, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<DocumentContactOptionsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState<number | null>(null);
  const [searchError, setSearchError] = useState('');
  const [openError, setOpenError] = useState('');
  const [revision, setRevision] = useState(0);
  const opening = useRef(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setSearchError('');
    setPage(null);
    const timer = setTimeout(() => {
      listDocumentContactOptions(search.trim(), offset)
        .then((result) => { if (!cancelled) setPage(result); })
        .catch(() => { if (!cancelled) setSearchError('Impossibile cercare i clienti. Riprova.'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, search, offset, revision]);

  const openDossier = async (contactId: number) => {
    if (opening.current) return;
    opening.current = true;
    setOpeningId(contactId);
    setOpenError('');
    try {
      const space = await ensureContactDocumentSpace(contactId);
      onSelect(space);
      onOpenChange(false);
    } catch (error: any) {
      const status = error.response?.status;
      setOpenError(status === 403
        ? 'Non hai i permessi per aprire o inizializzare questo dossier.'
        : status === 409
          ? 'Il dossier è chiuso o rimosso. Contatta un amministratore.'
          : status === 404
            ? 'Cliente non più disponibile. Aggiorna la ricerca.'
            : 'Impossibile aprire il dossier. Riprova selezionando il cliente.');
    } finally {
      opening.current = false;
      setOpeningId(null);
    }
  };

  return <Dialog open={open} onOpenChange={(next) => { if (!opening.current) onOpenChange(next); }}>
    <DialogContent className="document-dialog bg-[var(--hexa-modal-bg)] text-[var(--hexa-text-primary)]">
      <DialogHeader>
        <DialogTitle>Apri dossier cliente</DialogTitle>
        <DialogDescription>Scegli un cliente. Se il dossier non esiste, verrà creato vuoto se disponi dei permessi necessari.</DialogDescription>
      </DialogHeader>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--hexa-text-muted)]" />
        <Input
          autoFocus aria-label="Cerca cliente per dossier" placeholder="Nome, azienda, codice fiscale o partita IVA"
          value={search} disabled={openingId !== null}
          onChange={(event) => { setSearch(event.target.value); setOffset(0); setOpenError(''); }}
          className="h-10 rounded-lg border-[var(--hexa-border)] bg-[var(--hexa-surface-elevated)] pl-10 pr-10 text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)]"
        />
        {search && <Button type="button" variant="ghost" size="icon" aria-label="Azzera ricerca clienti" disabled={openingId !== null} className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2" onClick={() => { setSearch(''); setOffset(0); }}><X className="h-4 w-4" /></Button>}
      </div>
      {openError && <p role="alert" className="text-sm text-red-500">{openError}</p>}
      <div className="max-h-[45dvh] min-h-32 space-y-1 overflow-y-auto" aria-busy={loading}>
        {loading ? <p role="status" className="flex items-center gap-2 py-4 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Ricerca clienti...</p>
          : searchError ? <div role="alert" className="space-y-2 py-4 text-sm"><p>{searchError}</p><Button variant="outline" onClick={() => setRevision((value) => value + 1)}>Riprova</Button></div>
            : page?.results.length === 0 ? <p className="py-4 text-sm text-[var(--hexa-text-muted)]">{search.trim() ? 'Nessun cliente trovato.' : 'Nessun cliente disponibile. I dossier esistenti sono visibili secondo i tuoi permessi.'}</p>
              : page?.results.map((contact) => <button
                type="button" key={contact.id} disabled={openingId !== null || !contact.available}
                aria-label={`Apri dossier di ${contact.name}`}
                className="flex w-full items-center gap-3 rounded-lg border border-[var(--hexa-border)] p-3 text-left hover:bg-[var(--hexa-surface-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => void openDossier(contact.id)}
              >
                {contact.contact_type === 'company' ? <Building2 className="h-5 w-5 shrink-0" /> : <UserRound className="h-5 w-5 shrink-0" />}
                <span className="min-w-0 flex-1"><span className="block truncate font-medium">{contact.name}</span><span className="text-xs text-[var(--hexa-text-muted)]">{!contact.available ? 'Dossier chiuso o rimosso' : contact.has_dossier ? 'Apri dossier' : 'Apri un dossier vuoto'}</span></span>
                {openingId === contact.id ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <FolderOpen className="h-4 w-4 shrink-0" />}
              </button>)}
      </div>
      {!loading && page && page.count > 0 && <div className="flex items-center justify-between gap-2 text-xs text-[var(--hexa-text-muted)]">
        <span>{offset + 1}–{Math.min(offset + page.results.length, page.count)} di {page.count}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={!page.previous || openingId !== null} onClick={() => setOffset((value) => Math.max(0, value - 20))}>Precedenti</Button>
          <Button variant="outline" size="sm" disabled={!page.next || openingId !== null} onClick={() => setOffset((value) => value + 20)}>Successivi</Button>
        </div>
      </div>}
    </DialogContent>
  </Dialog>;
}
